import React from "react";
import { MoreHorizontal, Pencil, Trash2, Move as MoveIcon, Pin } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

/**
 * Shared "•••" moderation menu used on discussion/thread pages.
 *
 * Renders a small icon button that opens a dropdown of Edit / Move / Pin / Delete
 * actions. The parent owns all state (edit mode, modal open, etc.) — this
 * component just wires clicks. Items are omitted when their handler is undefined,
 * so a native discussion (no pin, no legacy move) only shows Edit / Move / Delete.
 *
 * Backend still enforces authorization on every action; this menu is purely a UI
 * shortcut for users who already have the permission.
 */
export default function ModActionsMenu({
  onEdit,
  onMove,
  onTogglePin,
  isPinned = false,
  onDelete,
  align = "end",
  testidPrefix = "mod-menu",
  label = "Moderasi",
}) {
  const anyAction = onEdit || onMove || onTogglePin || onDelete;
  if (!anyAction) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={label}
        data-testid={`${testidPrefix}-trigger`}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-text-muted hover:bg-brand-muted hover:text-brand-text transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
      >
        <MoreHorizontal className="w-4 h-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-48" data-testid={`${testidPrefix}-content`}>
        {onEdit && (
          <DropdownMenuItem onSelect={onEdit} data-testid={`${testidPrefix}-edit`} className="cursor-pointer">
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </DropdownMenuItem>
        )}
        {onMove && (
          <DropdownMenuItem onSelect={onMove} data-testid={`${testidPrefix}-move`} className="cursor-pointer">
            <MoveIcon className="w-4 h-4 mr-2" /> Pindahkan ke Ruang lain
          </DropdownMenuItem>
        )}
        {onTogglePin && (
          <DropdownMenuItem onSelect={onTogglePin} data-testid={`${testidPrefix}-pin`} className="cursor-pointer">
            <Pin className="w-4 h-4 mr-2" /> {isPinned ? "Unpin" : "Pin"}
          </DropdownMenuItem>
        )}
        {onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} data-testid={`${testidPrefix}-delete`} className="cursor-pointer text-rose-600 focus:text-rose-700">
              <Trash2 className="w-4 h-4 mr-2" /> Hapus
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
